import React from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, BookOpen } from "lucide-react";

/**
 * @typedef {Object} KnowledgeArticle
 * @property {string} slug
 * @property {string} title
 * @property {string} excerpt
 * @property {string} category
 * @property {string} [cover]
 * @property {number} read_time
 * @property {string} author
 */

/**
 * @typedef {Object} KnowledgeResultCardProps
 * @property {KnowledgeArticle} article
 * @property {string} [className]
 */

/**
 * Knowledge article card extracted from Knowledge.jsx's inline grid mapping.
 * Wraps the entire card in a router Link — caller renders this inside a grid,
 * one card per article.
 * @param {KnowledgeResultCardProps} props
 */
export default function KnowledgeResultCard({ article, className = "" }) {
  const a = article;
  return (
    <Link to={`/knowledge/${a.slug}`}>
      <Card className={`overflow-hidden border-border hover:border-primary/40 hover:-translate-y-1 transition-all h-full ${className}`}>
        {a.cover && (
          <img src={a.cover} alt={a.title} className="w-full h-44 object-cover" />
        )}
        <div className="p-5">
          <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
            {a.category}
          </Badge>
          <h3 className="mt-3 font-display text-lg font-semibold leading-snug text-balance">
            {a.title}
          </h3>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-3">
            {a.excerpt}
          </p>
          <div className="mt-4 flex items-center gap-3 text-xs text-muted-foreground">
            <Clock className="h-3.5 w-3.5" aria-hidden="true" />
            <span>{a.read_time} min read</span>
            <span aria-hidden="true">·</span>
            <BookOpen className="h-3.5 w-3.5" aria-hidden="true" />
            <span>{a.author}</span>
          </div>
        </div>
      </Card>
    </Link>
  );
}
